import React, { useEffect, useMemo, useState } from 'react'
import { cn, toCamelCase } from '@utils/safeTs'
import { getUltimoLogPosizione } from '@utils/gestione/ordini'
import styles from './FlussoPersonalizzazioneApprovazioneBozzePosizione.module.sass'
import { Button, CronologiaButton, FormField, FormTextarea, Alert, MyAccountImagePreview } from '@components/atoms'
import { Checkmark, Close, Info, Download } from '@components/icons'
import { OrdineDetailType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneType } from '@utils/types'
import useTrans from '@hooks/useTrans'
import MyAccountStatusLogModal from '../MyAccountStatusLogModal/MyAccountStatusLogModal'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { api } from '@utils/index'
import { Form, Formik } from 'formik'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { useRouter } from 'next/router'
import FlussoPersonalizzazionePosizione from '../FlussoPersonalizzazionePosizione/FlussoPersonalizzazionePosizione'
import FlussoPersonalizzazioneNotaValue from '@components/atoms/FlussoPersonalizzazioneNotaValue/FlussoPersonalizzazioneNotaValue'
import FlussoPersonalizzazionePosizioneConfigurazione from '../FlussoPersonalizzazionePosizioneConfigurazione/FlussoPersonalizzazionePosizioneConfigurazione'
import FlussoPersonalizzazionePosizioneLoghi from '../FlussoPersonalizzazionePosizioneLoghi/FlussoPersonalizzazionePosizioneLoghi'


interface Props {
  posizione: PosizionePersonalizzazioneType
  personalizzazione: PersonalizzazioneOrdineType
  setOrdine: (p: OrdineDetailType) => void
  setStatus: ((v: '' | 'unsaved' | 'completed') => void)
  personalizzazioniOpen?: number[]
  setPersonalizzazioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  posizioniOpen: number[]
  setPosizioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  className?: string
  condizioniServizioUrl?: string | null
}

const FlussoPersonalizzazioneApprovazioneBozzePosizione = (props: Props) => {

  const { 
    posizione,
    personalizzazione,
    setOrdine,
    setStatus,
    posizioniOpen,
    setPosizioniOpen,
    className = '',
    condizioniServizioUrl = null,
  } = props
  
  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const userType = 'cliente' // in approvazione sono sempre 'cliente' (potrebbe non essere nemmeno loggato)

  const getIsEditable = (pos, userType) => pos.statoPosizionePersonalizzazione === 'BOZZA_INVIATA' && ['admin', 'commerciale', 'cliente', undefined].includes(userType)
  const isEditable = useMemo(() => getIsEditable(posizione, userType), [userType, posizione])

  const [logOpen, setLogOpen] = useState(false)
  const [edited, setEdited] = useState(false)
  const [loghiEdited, setLoghiEdited] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const status = useMemo(() => edited ? 'unsaved' : '', [edited])
  
  const getInitialValues = (p) => ({
    note: '',
    loghi: p.loghi?.map(l => ({
      logo: l,
      fileLogo: undefined,
    })) || []
  })   

  const initialValues = getInitialValues(posizione)

  // Salvataggio logo
  const save = (v) => {
    let formData = new FormData()
    let logoIds = []
    Object.keys(v).forEach((k) => {
      if (k === 'loghi' && Array.isArray(v[k])) {
        v[k].forEach((logo) => {
          if (logo.logo?.pk) logoIds.push(logo.logo.pk)
          if (logo.fileLogo) formData.append('fileLogo', logo.fileLogo)
        })
        formData.append('logoIds', logoIds.join(","))
      } else {
        formData.append(k, v[k])
      }
    })
    formData.append("status", submitStatus)
    api
      .put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${posizione.id}/update_status/`, formData, {
        params: {
          ...(hashParam ? { hash: hashParam } : {}),
        },
      })
      .then((res) => {
        const newOrdine = toCamelCase(res.data) as OrdineDetailType
        setOrdine(newOrdine)
        setLoghiEdited(false)
        setEdited(false)
        displayNotification(
          `posizione_aggiornata_${posizione.id}`, 
          t("Posizione {0} aggiornata correttamente", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'success'
        )
      })
      .catch((res) => {
        console.log('ko', res)
        displayNotification(
          `posizione_aggiornata_error_${posizione.id}`, 
          t("Errore durante l'aggiornamento della posizione {0}", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'error'
        )
      })
  }

  const onValueChange = (e, handleChange) => {
    handleChange(e)
    setEdited(true)
  }

  useEffect(() => {
    setStatus(status)
  }, [status])

  return (
    <FlussoPersonalizzazionePosizione
      status={status}
      posizione={posizione} 
      posizioniOpen={posizioniOpen}
      setPosizioniOpen={setPosizioniOpen}
      userType={userType}
      className={cn(styles.root, posizioniOpen.includes(posizione.id) ? styles['root--open'] : '', isEditable ? styles['root--editable'] : '', className)}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <Form className={styles.form}>
            <FlussoPersonalizzazionePosizioneConfigurazione posizione={posizione} personalizzazione={personalizzazione} />
            <div className={styles.row}>
              <div className={cn(styles.section, styles['section--draft'])}>
                <p className={styles.sectionTitle}>{t('Bozza')}</p>
                <div className={cn(styles.draft, !posizione.bozzaDigitaleImage ? styles['draft--empty'] : '')}>
                  <MyAccountImagePreview 
                    id={`${posizione.id}_bozza`}
                    url={posizione.statoPosizionePersonalizzazione !== 'BOZZA_SALVATA' ? posizione.bozzaDigitaleImage?.url : null}
                    downloadUrl={null}
                    zoomUrl={posizione.statoPosizionePersonalizzazione !== 'BOZZA_SALVATA' ? posizione.bozzaDigitaleImage?.url : null}
                    name={posizione.bozzaDigitaleImage?.name}
                    size="lg"
                    previewName={t("bozza")}
                    className={styles.draftPreview}
                  />
                </div>
                {!!posizione.bozzaDigitale?.url && (posizione.statoPosizionePersonalizzazione !== 'BOZZA_SALVATA') && (
                  <Button 
                    label={t("Vedi e scarica bozza")}
                    size="small"
                    href={posizione.bozzaDigitale.url}
                    newPage
                    download
                    variant="secondary"
                    icon={<Download />}
                    icon_pos='left'
                    className={styles.draftDownload}
                  />
                )}
              </div>

              <FlussoPersonalizzazionePosizioneLoghi 
                posizione={posizione}
                values={values}
                setFieldValue={setFieldValue}
                isEditable={isEditable}
                variant='approvazione'
                edited={edited}
                setEdited={setEdited}
                loghiEdited={loghiEdited}
                setLoghiEdited={setLoghiEdited}
                className={cn(styles.section, styles['section--loghi'])}
              />
                
              <div className={styles.lastCol}>
                <div className={styles.note}>
                  <div className={cn(styles.section, styles['section--notaCliente'])}>
                    <FlussoPersonalizzazioneNotaValue 
                      header={
                        <div className={styles.sectionTitle}>
                          <span>{t('Nota stampa')}</span>
                          <CronologiaButton onClick={() => setLogOpen(true)} />
                        </div>
                      }
                      log={getUltimoLogPosizione(posizione.log, 'BOZZA_INVIATA')}
                      placeholder={t("Nessuna nota inserita dal fornitore")}
                      variant='accent'
                      className={styles.noteFornitoreValue}
                    />
                  </div>
                  <div className={cn(styles.section, styles['section--notaCaricamento'])}>
                    {isEditable && <p className={styles.sectionTitle}>{t('Nota per rifiutare')}</p>}
                    {isEditable ? (
                      <FormField className={styles.notaCaricamentoField}>
                        <FormTextarea
                          id={`${posizione.id}_nota_caricamento`}
                          name="note"
                          value={values.note}
                          placeholder={t("Aggiungi una nota per spiegare le modifiche al nostro reparto grafico...")}
                          onChange={(e) => onValueChange(e, handleChange)}
                        />
                      </FormField>
                    ) : (
                      <FlussoPersonalizzazioneNotaValue
                        header={<p className={styles.sectionTitle}>{t('Nota')}</p>}
                        log={getUltimoLogPosizione(posizione.log, posizione.statoPosizionePersonalizzazione)}
                        placeholder={t("Nessuna nota inserita dal cliente")}
                        className={styles.notaClienteValue}
                      />
                    )}
                  </div>
                  {isEditable && (
                    <div className={styles.ctaBox}>
                      {loghiEdited ? (
                        <Button 
                          type="submit"
                          variant="primary" 
                          size="medium" 
                          disabled={isSubmitting}
                          label={t('Richiedi nuova bozza')}
                          onClick={() => setSubmitStatus('BOZZA_RIFIUTATA')}
                        />
                      ) : (
                        <>
                          <Button 
                            type="submit"
                            variant="primary" 
                            colorVariant="success"
                            size="medium" 
                            label={t('Approva bozza')}
                            icon={<Checkmark />} 
                            icon_pos='right'
                            disabled={isSubmitting}
                            // disabled={!values.note} 
                            onClick={() => setSubmitStatus('BOZZA_APPROVATA')}
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            colorVariant="danger"
                            size="medium"
                            label={t('Rifiuta bozza')}
                            icon={<Close />}
                            icon_pos='right'
                            disabled={!values.note || isSubmitting}
                            onClick={() => setSubmitStatus('BOZZA_RIFIUTATA')}
                          />
                          <Alert 
                            variant="muted" 
                            styleVariant='basic' 
                            icon={<Info />} 
                            placeholder={t("Per rifiutare una bozza è obbligatorio lasciare una nota/commento")} 
                            size='xs'
                            className={styles.alertSave} 
                          />
                        </> 
                      )}                         
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.disclaimer}>
              <p><strong>{t("IL DISEGNO DELL’ARTICOLO HA VALORE PURAMENTE ILLUSTRATIVO")}</strong></p>
              <p>
                {t("Verificare con attenzione che il prodotto sia quello scelto al momento dell’ordine e che l’impostazione dei contenuti di stampa (nomi, telefono, indirizzo, logo, ecc.) siano corretti. La richiesta massima di modifiche alle bozze grafiche è di 3, dalla 4a bozza verrà addebitato un costo aggiuntivo per ogni bozza successiva. Una volta approvata la bozza, non sarà più possibile modificare o annullare l’ordine e la data di partenza sarà aggiornata in base al giorno di approvazione. Il colore dei prodotti gadget, rispetto alla bozza inviata, potrebbe variare anche all’interno dello stesso stock a magazzino. Faremo il possibile per ottenere una resa ottimale del colore di stampa, che però potrebbe variare rispetto alla visualizzazione video.")}
                {!!condizioniServizioUrl && (
                  <>
                    {' '}
                    {t('Per qualsiasi informazione aggiuntiva, vedi:')}{' '}
                    <a href={condizioniServizioUrl} target="_blank" rel="noopener noreferrer">
                      {t('condizioni di vendita')}
                    </a>
                  </>
                )}
              </p>
            </div>
          </Form>
        )}
      </Formik>
      <MyAccountStatusLogModal id={`status_log_modal_${posizione.id}`} open={logOpen} onClose={() => setLogOpen(false)} posizione={posizione} userType={userType} />
    </FlussoPersonalizzazionePosizione>
  )
}

export default FlussoPersonalizzazioneApprovazioneBozzePosizione
